<template>
  <div id="homeowner-resources" class="mx-auto max-w-screen-xl">
    <MoleculesLoadingOverlay v-if="loading" />
    <Organisms v-else :organisms="organisms" />
    <OrganismsModalForm>
      <h2 class="mb-4 text-4xl font-semibold text-black font-archer">
        Request a warranty service
      </h2>
      <p v-if="!isFormSubmitted" class="text-lg text-gray-500">
        Please provide us your information and we will get back to you.
      </p>
      <FormsHubSpotForm
        :form="{ id: 'b560c467-6962-48d3-98e1-59962a67e578' }"
        @submit="isFormSubmitted = true"
      />
    </OrganismsModalForm>
  </div>
</template>

<style>
#homeowner-resources {
  #organism-1.brand-window-text-overlay {
    @apply mb-0;
    .content {
      @apply w-full max-w-3xl;
      p {
        @apply text-xl max-w-2xl mx-auto;
      }
    }
  }
  #caring-for-your-new-home.tabbed-content-generic {
    @apply mb-0;

    .tabbed-content > header {
      @apply pt-20;
      .header-content {
        @apply max-w-5xl;
        h2 {
          @apply mb-4;
        }
        p {
          @apply text-bc-bright-blue text-left mb-12;
        }
      }
    }

    .tabs-component-tab {
      /* @apply max-w-none; */
    }
    .tabs-component-panel .wysiwyg-block {
      @apply mx-auto max-w-5xl pt-24 pb-24 px-8;

      &.container-full-width > div {
        @apply max-w-5xl mx-auto;
      }

      h2 {
        @apply text-4xl text-bc-le-gray font-archer font-semibold after:content-[''] after:block after:w-[82px] after:h-[6px] after:bg-bc-purple after:my-8;
      }

      h3 {
        @apply text-black font-archer font-bold text-2xl mb-8;
        a {
          @apply text-bc-blue underline;
        }
      }

      p {
        @apply text-lg mb-8;
        a {
          @apply text-bc-blue underline;
        }
      }

      ul {
        @apply mb-8 list-disc;

        li {
          @apply mb-4;
        }
      }

      .buttons {
        @apply justify-center;
      }
    }

    .tab-warranty-services-wysiwyg-block-2 {
      /* @apply bg-bc-gray; */
      & > div {
        @apply px-8;
      }
    }

    #tab-warranty-services-wysiwyg-block-5 {
      @apply py-12;
      p {
        @apply text-white text-xs;
      }
    }

    #tab-resources---lower-maintenance-wysiwyg-block-1 {
      @apply pb-0 mb-0;

      h2 {
        @apply after:mx-auto;
      }
    }

    .accordion {
      @apply max-w-5xl mx-auto;

      .accordion-content {
        p {
          @apply mb-4;
        }

        ul {
          @apply list-disc pl-16;

          li {
            @apply mb-2;
          }
        }
      }
    }
  }
  #organism-3 {
    @apply bg-gradient-to-b from-zinc-100 to-white pt-20;

    p {
      @apply text-xs text-gray-500 pb-4 mx-auto max-w-5xl px-8;
    }
  }

  .warranty-grid {
    @apply relative;
  }

  .warranty-grid .image {
    @apply md:absolute static md:block hidden top-0 right-0;
  }

  .grid-image-padding {
    @apply md:pr-[210px] pr-0;
  }

}
</style>

<script setup>
import { useAsyncQuery } from "#imports";
import { GET_ORGANISMS } from "~/graphql/GetOrganisms";
import { storeToRefs } from "pinia";
const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
const modalForm = useModalForm();
const isFormSubmitted = ref(false);

breadcrumbsList.value = [{ title: "Homeowner Resources", link: "" }];

const { data, pending, error } = await useAsyncQuery(GET_ORGANISMS, {
  uri: "/contact-us/homeowner-resources/",
});

const organisms = computed(() => {
  return data.value?.organisms?.hlOrganisms?.hlOrganism ?? [];
});

const loading = pending;

const scrollToTabs = (currentHash) => {
  const tabs = [
    "#tab-warranty-services",
    "#tab-resources-lower-maintenance",
    "#tab-how-to-video-library",
  ];
  if (tabs.includes(currentHash)) {
    const target = document.querySelector("#caring-for-your-new-home");
    if (target) {
      target.scrollIntoView(true);
      return true;
    }
  }
  return false;
};

const mapUrlHash = (currentHash) => {
  const hashMappings = {
    "#warranty-services": "#tab-warranty-services",
    "#resources-lower-maintenance": "#tab-resources---lower-maintenance",
    "#how-to-video-library": "#tab-how-to-video-library",
  };
  if (hashMappings.hasOwnProperty(currentHash)) {
    const newHash = hashMappings[currentHash];
    window.location.hash = newHash;
    scrollToTabs(newHash);
    return true;
  }
  return false;
};

onMounted(() => {
  console.log("Component mounted");
  window.addEventListener(
    "hashchange",
    () => {
      console.log("Hash changed:", window.location.hash);
      if (window.location.hash === "#modal-form") {
        modalForm.openModal();
        window.location.hash = "";
      }
    },
    false,
  );

  addTriggers();
});

watch(loading, async (newLoading, oldLoading) => {
  if (oldLoading && !newLoading) {
    await nextTick();
    addTriggers();

    const urlHash = window.location.hash;
    const handleTabsHash = scrollToTabs(urlHash) ? true : mapUrlHash(urlHash);

    if (urlHash && !handleTabsHash) {
      document.querySelector(urlHash).scrollIntoView(true);
    }
  }
});

const addTriggers = () => {
  console.log("Adding triggers");
  const modalFormTriggers = document.querySelectorAll("a[href$='#modal-form'], a[href*='/contact-us/homeowner-resources/#modal-form']");
  console.log("Found triggers:", modalFormTriggers);

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      console.log("Modal form trigger clicked");
      modalForm.openModal();
    });
  });
};

onUpdated(() => {
  console.log("Component updated");
  addTriggers();
});
</script>
